/**
 * Partner részletek oldal — szerkesztés + kapcsolattartók + statisztikák
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get, post, patch, del } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, Tabs } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useFKeyBar } from '../../components/layout/FKeyBar';

interface Partner {
  id: string; name: string; tax_number: string; kind: string;
  is_customer: boolean; is_supplier: boolean; is_archived: boolean;
  bank_account_number: string; email: string; notes: string; created_at: string;
  price_list_id?: string | null;
  payment_terms_days?: number | null;
  is_credit_account?: boolean;
  credit_limit?: number | null;
  credit_warning_threshold_percent?: string | null;
  addresses?: any[]; tags?: any[];
}

interface Contact {
  id: string; name: string; position: string; email: string; phone: string;
  addressForm: string; isPrimary: boolean; note: string;
  isBilling?: boolean; isQuoteRequest?: boolean; isNewsletter?: boolean;
  // snake_case aliases (some endpoints return these)
  address_form?: string; is_primary?: boolean; is_billing?: boolean; is_quote_request?: boolean; is_newsletter?: boolean; notes?: string;
}

interface Stats {
  orderCount: number; invoiceCount: number; totalRevenue: number;
}

export default function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stats, setStats] = useState<Stats>({ orderCount: 0, invoiceCount: 0, totalRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        get<Partner>(`/partners/${id}`),
        get<Contact[]>(`/partners/${id}/contacts`).catch(() => []),
      ]);
      setPartner(p);
      setContacts(Array.isArray(c) ? c : (c as any)?.data || []);
      // Stats from related entities
      try {
        const orders = await get<any>('/sales-orders?partnerId=' + id);
        const invoices = await get<any>('/invoices?partner_id=' + id);
        const oList = Array.isArray(orders) ? orders : orders?.data || [];
        const iList = Array.isArray(invoices) ? invoices : invoices?.data || [];
        setStats({
          orderCount: oList.length,
          invoiceCount: iList.length,
          totalRevenue: iList.reduce((s: number, i: any) => s + Number(i.subtotal_gross || 0), 0),
        });
      } catch { /* silent */ }
    } catch { toast('Partner betöltése sikertelen.', 'error'); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useFKeyBar([
    { key: 'F2', label: 'Szerkesztés', action: () => setEditing(true) },
    { key: 'F3', label: 'Új kapcsolattartó', action: () => { setEditContact(null); setShowContactModal(true); } },
    { key: 'F5', label: 'Frissít', action: load },
    { key: 'F8', label: 'Vissza', action: () => navigate('/partnerek') },
  ]);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>;
  if (!partner) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--red)' }}>Partner nem található.</div>;

  const formatHuf = (v: number) => Math.round(v).toLocaleString('hu-HU') + ' Ft';
  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const defaultContact = contacts.find(c => c.isPrimary || c.is_primary);

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <button onClick={() => navigate('/partnerek')} style={{ fontSize: '13px', color: 'var(--slate)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '8px' }}>← Vissza a partnerlistához</button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--navy)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{partner.name}</h1>
            <div style={{ fontSize: '13px', color: 'var(--slate)', marginTop: '4px' }}>
              {partner.tax_number || '—'} · {partner.is_customer && 'Vevő'}{partner.is_customer && partner.is_supplier && ' + '}{partner.is_supplier && 'Beszállító'}
              {partner.is_archived && <Badge variant="error" style={{ marginLeft: '8px' }}>Archivált</Badge>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {partner.is_archived ? (
              <Button variant="ghost" onClick={async () => {
                try { await post(`/partners/${partner.id}/restore`, {}); toast('Partner visszaállítva.', 'success'); load(); } catch { toast('Visszaállítás sikertelen.', 'error'); }
              }}>↩ Visszaállítás</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setEditing(true)}>Szerkesztés</Button>
                <Button onClick={() => navigate(`/megrendelesek?partnerId=${partner.id}&partnerName=${encodeURIComponent(partner.name)}`)}>+ Megrendelés</Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* KPI kártyák */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <Card><div style={{ textAlign: 'center', padding: '8px' }}>
          <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', fontWeight: 700 }}>Megrendelések</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--navy)' }}>{stats.orderCount}</div>
        </div></Card>
        <Card><div style={{ textAlign: 'center', padding: '8px' }}>
          <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', fontWeight: 700 }}>Számlák</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--navy)' }}>{stats.invoiceCount}</div>
        </div></Card>
        <Card><div style={{ textAlign: 'center', padding: '8px' }}>
          <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', fontWeight: 700 }}>Bevétel</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--orange)' }}>{formatHuf(stats.totalRevenue)}</div>
        </div></Card>
        <Card><div style={{ textAlign: 'center', padding: '8px' }}>
          <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', fontWeight: 700 }}>Regisztrálva</div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>{formatDate(partner.created_at)}</div>
        </div></Card>
      </div>

      <Tabs tabs={[
        { label: 'Áttekintés', content: <OverviewTab partner={partner} /> },
        { label: `Kapcsolattartók (${contacts.length})`, content: (
          <ContactsTab
            contacts={contacts} partnerId={partner.id}
            onAdd={() => { setEditContact(null); setShowContactModal(true); }}
            onEdit={(c) => { setEditContact(c); setShowContactModal(true); }}
            onSetDefault={async (contactId) => {
              try { await post(`/partners/${partner.id}/contacts/${contactId}/set-primary`, {}); toast('Alapértelmezett beállítva.', 'success'); load(); } catch { toast('Hiba.', 'error'); }
            }}
            onDelete={async (contactId) => {
              try { await del(`/partners/${partner.id}/contacts/${contactId}`); toast('Kapcsolattartó törölve.', 'success'); load(); } catch { toast('Hiba.', 'error'); }
            }}
          />
        )},
        { label: 'Megjegyzések', content: <NotesTab partner={partner} onSave={async (notes) => {
          try { await patch(`/partners/${partner.id}`, { notes }); toast('Megjegyzés mentve.', 'success'); load(); } catch { toast('Hiba.', 'error'); }
        }} /> },
      ]} />

      {/* Partner szerkesztés modal */}
      {editing && <EditPartnerModal partner={partner} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); toast('Partner frissítve.', 'success'); }} />}

      {/* Kapcsolattartó modal */}
      <ContactModal
        isOpen={showContactModal} onClose={() => setShowContactModal(false)}
        contact={editContact} partnerId={partner.id}
        onSave={async (data) => {
          if (editContact) { await patch(`/partners/${partner.id}/contacts/${editContact.id}`, data); }
          else { await post(`/partners/${partner.id}/contacts`, data); }
          setShowContactModal(false); load();
          toast(editContact ? 'Kapcsolattartó frissítve.' : 'Kapcsolattartó hozzáadva.', 'success');
        }}
      />
    </div>
  );
}

// ── Áttekintés tab ──
function OverviewTab({ partner }: { partner: Partner }) {
  const [priceListName, setPriceListName] = useState<string | null>(null);
  const addr = partner.addresses?.[0];
  const fmtAddr = (a: any) => {
    const parts = [a.postal_code, a.city].filter(Boolean);
    const street = [a.street_name, a.street_type, a.house_number].filter(Boolean).join(' ');
    if (street) parts.push(street);
    const extra = [a.building && `${a.building} ép.`, a.floor && `${a.floor}. em.`, a.door && `${a.door}. ajtó`].filter(Boolean);
    let result = parts.join(', ');
    if (extra.length) result += ', ' + extra.join(' ');
    return result;
  };
  useEffect(() => {
    if (!partner.price_list_id) { setPriceListName(null); return; }
    get<any>(`/price-lists/${partner.price_list_id}`).then(pl => setPriceListName(pl?.name || null)).catch(() => {});
  }, [partner.price_list_id]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
          <div className="row-li"><div className="ri-main"><div className="ri-t">Cégnév</div><div className="ri-s">{partner.name}</div></div></div>
          <div className="row-li"><div className="ri-main"><div className="ri-t">Adószám</div><div className="ri-s">{partner.tax_number || '—'}</div></div></div>
          <div className="row-li"><div className="ri-main"><div className="ri-t">Típus</div><div className="ri-s">{partner.is_customer ? 'Vevő' : ''}{partner.is_customer && partner.is_supplier ? ' + ' : ''}{partner.is_supplier ? 'Beszállító' : ''}</div></div></div>
          <div className="row-li"><div className="ri-main"><div className="ri-t">E-mail</div><div className="ri-s">{partner.email || '—'}</div></div></div>
          <div className="row-li"><div className="ri-main"><div className="ri-t">Bankszámla</div><div className="ri-s">{partner.bank_account_number || '—'}</div></div></div>
          <div className="row-li"><div className="ri-main"><div className="ri-t">Alapért. árlista</div><div className="ri-s">{priceListName || (partner.price_list_id ? '...' : '—')}</div></div></div>
          {partner.payment_terms_days != null && (
            <div className="row-li"><div className="ri-main"><div className="ri-t">Fizetési határidő</div><div className="ri-s">{partner.payment_terms_days} nap</div></div></div>
          )}
          {partner.is_credit_account && (
            <div className="row-li"><div className="ri-main"><div className="ri-t">Hitelkeret</div><div className="ri-s">{partner.credit_limit ? Math.round(Number(partner.credit_limit)).toLocaleString('hu-HU') + ' Ft' : '—'}</div></div></div>
          )}
          {addr && <div className="row-li" style={{ gridColumn: '1 / -1' }}><div className="ri-main"><div className="ri-t">Székhely</div><div className="ri-s">{fmtAddr(addr)}</div></div></div>}
        </div>
      </Card>
      {partner.tags && partner.tags.length > 0 && (
        <Card>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Címkék</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {partner.tags.map((t: any) => (
              <span key={t.id} style={{ padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: 600, background: t.color ? t.color + '22' : 'var(--mist)', color: t.color || 'var(--navy)', border: `1px solid ${t.color || 'var(--line)'}` }}>
                {t.name}
              </span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Kapcsolattartók tab ──
function ContactsTab({ contacts, partnerId, onAdd, onEdit, onSetDefault, onDelete }: {
  contacts: Contact[]; partnerId: string;
  onAdd: () => void; onEdit: (c: Contact) => void; onSetDefault: (id: string) => void; onDelete: (id: string) => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <Button size="sm" onClick={onAdd}>+ Új kapcsolattartó</Button>
      </div>
      {contacts.length === 0 ? (
        <Card><div style={{ padding: '32px', textAlign: 'center', color: 'var(--slate)', fontSize: '13px' }}>
          Még nincs kapcsolattartó. Az első hozzáadott automatikusan az alapértelmezett lesz.
        </div></Card>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {contacts.map(c => {
            const isPrimary = c.isPrimary || c.is_primary;
            const addrForm = (c as any).addressForm || c.address_form;
            const note = (c as any).note || c.notes;
            return (
              <Card key={c.id}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{c.name}</span>
                      {isPrimary && <Badge variant="success">Alapértelmezett</Badge>}
                      {(c.isBilling || c.is_billing) && <Badge variant="warning">Számlázási</Badge>}
                      {(c.isQuoteRequest || c.is_quote_request) && <Badge variant="info">Ajánlatkérési</Badge>}
                      {(c.isNewsletter || c.is_newsletter) && <Badge variant="default">Hírlevél</Badge>}
                      <Badge variant={addrForm === 'informal' ? 'info' : 'default'}>{addrForm === 'informal' ? 'Tegezés' : 'Magázás'}</Badge>
                    </div>
                    {c.position && <div style={{ fontSize: '12px', color: 'var(--slate)', marginBottom: '2px' }}>{c.position}</div>}
                    <div style={{ fontSize: '12px', color: 'var(--slate)' }}>
                      {c.email && <span>{c.email}</span>}
                      {c.email && c.phone && <span> · </span>}
                      {c.phone && <span>{c.phone}</span>}
                    </div>
                    {note && <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '4px', fontStyle: 'italic' }}>{note}</div>}
                  </div>
                  <ContactActions
                    isPrimary={isPrimary}
                    onSetDefault={() => onSetDefault(c.id)}
                    onEdit={() => onEdit(c)}
                    onDelete={() => onDelete(c.id)}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Kapcsolattartó ... menü ──
function ContactActions({ isPrimary, onSetDefault, onEdit, onDelete }: { isPrimary: boolean; onSetDefault: () => void; onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--slate)', padding: '4px 8px', borderRadius: 'var(--r)' }}
        onMouseOver={e => (e.currentTarget.style.background = 'var(--mist)')} onMouseOut={e => (e.currentTarget.style.background = 'none')}>
        ⋯
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 'var(--r)', boxShadow: '0 12px 30px -10px rgba(20,35,63,.3)', minWidth: '180px', padding: '6px', zIndex: 200 }}>
          <button className="dd-item" style={{ width: '100%', textAlign: 'left' }} onClick={() => { onEdit(); setOpen(false); }}>
            <span className="dot" /> Szerkesztés
          </button>
          {!isPrimary && (
            <button className="dd-item" style={{ width: '100%', textAlign: 'left' }} onClick={() => { onSetDefault(); setOpen(false); }}>
              <span className="dot" style={{ background: 'var(--orange)' }} /> Alapértelmezett kapcsolattartó
            </button>
          )}
          <div style={{ height: '1px', background: 'var(--line-2)', margin: '4px 0' }} />
          <button className="dd-item" style={{ width: '100%', textAlign: 'left', color: 'var(--red)' }} onClick={() => { onDelete(); setOpen(false); }}>
            <span className="dot" style={{ background: 'var(--red)' }} /> Törlés
          </button>
        </div>
      )}
    </div>
  );
}

// ── Megjegyzések tab ──
function NotesTab({ partner, onSave }: { partner: Partner; onSave: (notes: string) => void }) {
  const [notes, setNotes] = useState(partner.notes || '');
  return (
    <Card>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={6} className="form-input" style={{ resize: 'vertical', minHeight: '120px' }} placeholder="Belső megjegyzések a partnerhez..." />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
        <Button size="sm" onClick={() => onSave(notes)}>Mentés</Button>
      </div>
    </Card>
  );
}

// ── Partner szerkesztés modal ──
function EditPartnerModal({ partner, onClose, onSaved }: { partner: Partner; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: partner.name || '', taxNumber: partner.tax_number || '',
    isCustomer: partner.is_customer, isSupplier: partner.is_supplier,
    bankAccountNumber: partner.bank_account_number || '', email: partner.email || '', notes: partner.notes || '',
    priceListId: partner.price_list_id || '',
    paymentTermsDays: partner.payment_terms_days != null ? String(partner.payment_terms_days) : '',
    isCreditAccount: partner.is_credit_account || false,
    creditLimit: partner.credit_limit != null ? String(partner.credit_limit) : '',
  });
  const [priceLists, setPriceLists] = useState<{ id: string; name: string; is_default: boolean }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get<any[]>('/price-lists').then(data => setPriceLists(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value }));

  async function handleSave(e: React.FormEvent) {
    e.preventDefault(); setSaving(true);
    try {
      await patch(`/partners/${partner.id}`, {
        name: form.name, taxNumber: form.taxNumber || null,
        isCustomer: form.isCustomer, isSupplier: form.isSupplier,
        bankAccountNumber: form.bankAccountNumber || null,
        email: form.email || null, notes: form.notes || null,
        priceListId: form.priceListId || null,
        paymentTermsDays: form.paymentTermsDays !== '' ? parseInt(form.paymentTermsDays, 10) : null,
        isCreditAccount: form.isCreditAccount,
        creditLimit: form.isCreditAccount && form.creditLimit !== '' ? parseInt(form.creditLimit, 10) : null,
      });
      onSaved();
    } catch (err: any) { toast(err.message || 'Mentés sikertelen.', 'error'); } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Partner szerkesztése">
      <form onSubmit={handleSave} className="space-y-4">
        <Input label="Cégnév *" value={form.name} onChange={set('name')} required />
        <Input label="Adószám" value={form.taxNumber} onChange={set('taxNumber')} />
        <Input label="Bankszámlaszám" value={form.bankAccountNumber} onChange={set('bankAccountNumber')} />
        <Input label="E-mail" type="email" value={form.email} onChange={set('email')} placeholder="partner@ceg.hu" />
        <div style={{ display: 'flex', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
            <input type="checkbox" checked={form.isCustomer} onChange={e => setForm(f => ({ ...f, isCustomer: e.target.checked }))} /> Vevő
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
            <input type="checkbox" checked={form.isSupplier} onChange={e => setForm(f => ({ ...f, isSupplier: e.target.checked }))} /> Beszállító
          </label>
        </div>
        <div>
          <label className="form-label">Alapértelmezett árlista</label>
          <select
            value={form.priceListId}
            onChange={e => setForm(f => ({ ...f, priceListId: e.target.value }))}
            className="form-input"
          >
            <option value="">— Nincs / alapár —</option>
            {priceLists.map(pl => (
              <option key={pl.id} value={pl.id}>
                {pl.name}{pl.is_default ? ' (alapértelmezett)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Input label="Fizetési határidő (nap)" type="number" value={form.paymentTermsDays} onChange={e => setForm(f => ({ ...f, paymentTermsDays: e.target.value }))} placeholder="pl. 30" min={0} max={365} />
          <div />
        </div>
        <div style={{ padding: '12px', background: 'var(--mist)', borderRadius: 'var(--r)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', marginBottom: form.isCreditAccount ? '10px' : '0' }}>
            <input type="checkbox" checked={form.isCreditAccount} onChange={e => setForm(f => ({ ...f, isCreditAccount: e.target.checked }))} /> Hitelszámlás partner
          </label>
          {form.isCreditAccount && (
            <Input label="Hitelkeret (Ft)" type="number" value={form.creditLimit} onChange={e => setForm(f => ({ ...f, creditLimit: e.target.value }))} placeholder="pl. 500000" min={1} />
          )}
        </div>
        <div><label className="form-label">Megjegyzés</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="form-input" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '8px' }}>
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>Mentés</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Kapcsolattartó modal ──
function ContactModal({ isOpen, onClose, contact, partnerId, onSave }: {
  isOpen: boolean; onClose: () => void; contact: Contact | null; partnerId: string;
  onSave: (data: any) => Promise<void>;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', position: '', email: '', phone: '', addressForm: 'formal', notes: '', isBilling: false, isQuoteRequest: false, isNewsletter: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: contact?.name || '', position: contact?.position || '',
        email: contact?.email || '', phone: contact?.phone || '',
        addressForm: (contact as any)?.addressForm || contact?.address_form || 'formal', notes: (contact as any)?.note || contact?.notes || '',
        isBilling: !!(contact?.isBilling || contact?.is_billing),
        isQuoteRequest: !!(contact?.isQuoteRequest || contact?.is_quote_request),
        isNewsletter: !!(contact?.isNewsletter || contact?.is_newsletter),
      });
    }
  }, [isOpen, contact]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(f => ({ ...f, [key]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true);
    try {
      await onSave({
        name: form.name,
        position: form.position || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        addressForm: form.addressForm,
        note: form.notes || undefined,
        isPrimary: false,
        isBilling: form.isBilling,
        isQuoteRequest: form.isQuoteRequest,
        isNewsletter: form.isNewsletter,
      });
    } catch (err: any) { toast(err.message || 'Mentés sikertelen.', 'error'); }
    setSaving(false);
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={contact ? 'Kapcsolattartó szerkesztése' : '+ Új kapcsolattartó'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Név *" value={form.name} onChange={set('name')} required placeholder="Kovács Péter" />
        <Input label="Beosztás" value={form.position} onChange={set('position')} placeholder="Ügyvezető" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Input label="E-mail" type="email" value={form.email} onChange={set('email')} placeholder="kovacs@ceg.hu" />
          <Input label="Telefon" value={form.phone} onChange={set('phone')} placeholder="+36 30 123 4567" />
        </div>
        <div>
          <label className="form-label">Szerepek</label>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isBilling} onChange={e => setForm(f => ({ ...f, isBilling: e.target.checked }))} /> Számlázási
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isQuoteRequest} onChange={e => setForm(f => ({ ...f, isQuoteRequest: e.target.checked }))} /> Ajánlatkérési
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isNewsletter} onChange={e => setForm(f => ({ ...f, isNewsletter: e.target.checked }))} /> Hírlevél
            </label>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>A számlázási kapcsolattartónak küldődik a számla e-mailben.</div>
        </div>
        <div>
          <label className="form-label">Megszólítás</label>
          <div style={{ display: 'flex', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" name="addressForm" value="informal" checked={form.addressForm === 'informal'} onChange={() => setForm(f => ({ ...f, addressForm: 'informal' }))} /> Tegezés
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" name="addressForm" value="formal" checked={form.addressForm === 'formal'} onChange={() => setForm(f => ({ ...f, addressForm: 'formal' }))} /> Magázás
            </label>
          </div>
        </div>
        <div><label className="form-label">Megjegyzés</label>
          <textarea value={form.notes} onChange={set('notes')} rows={2} className="form-input" placeholder="Belső megjegyzés a kapcsolattartóhoz..." />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '8px' }}>
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>{contact ? 'Mentés' : 'Hozzáadás'}</Button>
        </div>
      </form>
    </Modal>
  );
}
